import { useState, useCallback, useRef } from 'react';
import { Upload, FileText, Loader2, CheckCircle2, AlertCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import type { ExtractedCVData, CVUploadAndExtractResponse } from '../types';

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.oasis.opendocument.text',
  'image/jpeg',
  'image/png',
  'image/tiff',
  'image/bmp',
  'image/webp',
];
const ACCEPTED_EXTENSIONS = '.pdf,.doc,.docx,.odt,.jpg,.jpeg,.png,.tiff,.tif,.bmp,.webp';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

type UploadStatus = 'idle' | 'uploading' | 'extracting' | 'success' | 'error';

interface CVUploaderProps {
  onExtracted: (data: ExtractedCVData) => void;
  onUploadComplete?: (response: CVUploadAndExtractResponse) => void;
  uploadFn: (file: File, onProgress?: (progress: number) => void) => Promise<CVUploadAndExtractResponse>;
  disabled?: boolean;
}

export function CVUploader({
  onExtracted,
  onUploadComplete,
  uploadFn,
  disabled = false,
}: CVUploaderProps) {
  const [status, setStatus] = useState<UploadStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [extractedData, setExtractedData] = useState<ExtractedCVData | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return 'Format non supporté. Utilisez PDF, DOCX, ou une image (JPEG, PNG).';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'Fichier trop volumineux. Maximum 10 Mo.';
    }
    return null;
  };

  const handleUpload = useCallback(
    async (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        setStatus('error');
        return;
      }

      setFileName(file.name);
      setError(null);
      setExtractedData(null);
      setStatus('uploading');
      setProgress(0);

      try {
        const response = await uploadFn(file, (uploadProgress) => {
          setProgress(uploadProgress * 0.3); // Upload is 30% of total
        });

        setStatus('extracting');
        setProgress(50);

        // Simulate extraction progress (actual extraction happens on server)
        const progressInterval = setInterval(() => {
          setProgress((prev) => Math.min(prev + 10, 90));
        }, 500);

        // Check extraction result
        if (response.extraction.success && response.extraction.data) {
          clearInterval(progressInterval);
          setProgress(100);
          setExtractedData(response.extraction.data);
          setStatus('success');
          onExtracted(response.extraction.data);
          onUploadComplete?.(response);
        } else {
          clearInterval(progressInterval);
          throw new Error(response.extraction.error || 'Extraction failed');
        }
      } catch (err: any) {
        setError(err.message || 'Une erreur est survenue');
        setStatus('error');
      }
    },
    [uploadFn, onExtracted, onUploadComplete]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      if (disabled || status === 'uploading' || status === 'extracting') return;

      const file = e.dataTransfer.files[0];
      if (file) {
        handleUpload(file);
      }
    },
    [handleUpload, disabled, status]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleUpload(file);
      }
    },
    [handleUpload]
  );

  const handleReset = () => {
    setStatus('idle');
    setProgress(0);
    setError(null);
    setFileName(null);
    setExtractedData(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const isProcessing = status === 'uploading' || status === 'extracting';

  return (
    <Card className="border-[var(--color-border)] bg-surface-elevated">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold text-ink flex items-center gap-2">
          <FileText className="h-5 w-5 text-bpa-accent" />
          Import CV
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Drop zone */}
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`
            relative border-2 border-dashed rounded-lg p-6 transition-all duration-200
            ${isDragOver ? 'border-bpa-accent bg-bpa-accent-light' : 'border-[var(--color-border)]'}
            ${disabled || isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-bpa-accent hover:bg-surface-sunken'}
            ${status === 'success' ? 'border-success bg-success-light' : ''}
            ${status === 'error' ? 'border-error bg-error-light' : ''}
          `}
          onClick={() => !disabled && !isProcessing && inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_EXTENSIONS}
            onChange={handleFileSelect}
            className="hidden"
            disabled={disabled || isProcessing}
          />

          <div className="flex flex-col items-center text-center">
            {status === 'idle' && (
              <>
                <div className="h-12 w-12 rounded-full bg-bpa-accent-light flex items-center justify-center mb-3">
                  <Upload className="h-6 w-6 text-bpa-accent" />
                </div>
                <p className="font-medium text-ink mb-1">
                  Glissez votre CV ici
                </p>
                <p className="text-sm text-[var(--color-text-secondary)]">
                  ou cliquez pour sélectionner un fichier
                </p>
                <p className="text-xs text-[var(--color-text-tertiary)] mt-2">
                  PDF, DOCX, JPEG, PNG - Max 10 Mo
                </p>
              </>
            )}

            {isProcessing && (
              <>
                <Loader2 className="h-10 w-10 text-bpa-accent animate-spin mb-3" />
                <p className="font-medium text-ink mb-1">
                  {status === 'uploading' ? 'Envoi en cours...' : 'Extraction des données...'}
                </p>
                <p className="text-sm text-[var(--color-text-secondary)] mb-3">
                  {fileName}
                </p>
                <Progress value={progress} className="w-full max-w-xs h-2" />
              </>
            )}

            {status === 'success' && (
              <>
                <CheckCircle2 className="h-10 w-10 text-success mb-3" />
                <p className="font-medium text-success mb-1">
                  Extraction réussie
                </p>
                <p className="text-sm text-[var(--color-text-secondary)]">
                  {fileName}
                </p>
              </>
            )}

            {status === 'error' && (
              <>
                <AlertCircle className="h-10 w-10 text-error mb-3" />
                <p className="font-medium text-error mb-1">
                  Erreur
                </p>
                <p className="text-sm text-[var(--color-text-secondary)]">
                  {error}
                </p>
              </>
            )}
          </div>

          {(status === 'success' || status === 'error') && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2"
              onClick={(e) => {
                e.stopPropagation();
                handleReset();
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Extracted data preview */}
        {extractedData && status === 'success' && (
          <div className="mt-4 p-4 bg-surface-sunken rounded-lg">
            <p className="text-xs font-semibold text-copper uppercase tracking-wider mb-3">
              Données extraites
            </p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-[var(--color-text-tertiary)]">Nom: </span>
                <span className="text-ink font-medium">
                  {extractedData.coordonnees_personnelles.prenom}{' '}
                  {extractedData.coordonnees_personnelles.nom}
                </span>
              </div>
              <div>
                <span className="text-[var(--color-text-tertiary)]">Poste: </span>
                <span className="text-ink font-medium">
                  {extractedData.titre_poste || '-'}
                </span>
              </div>
              <div>
                <span className="text-[var(--color-text-tertiary)]">Email: </span>
                <span className="text-ink">
                  {extractedData.coordonnees_personnelles.email || '-'}
                </span>
              </div>
              <div>
                <span className="text-[var(--color-text-tertiary)]">Tél: </span>
                <span className="text-ink">
                  {extractedData.coordonnees_personnelles.telephone || '-'}
                </span>
              </div>
              <div>
                <span className="text-[var(--color-text-tertiary)]">Ville: </span>
                <span className="text-ink">
                  {extractedData.coordonnees_personnelles.ville || '-'}
                </span>
              </div>
              <div>
                <span className="text-[var(--color-text-tertiary)]">Expériences: </span>
                <span className="text-ink">{extractedData.nbExp}</span>
              </div>
            </div>
            {extractedData.competences.length > 0 && (
              <div className="mt-3">
                <span className="text-[var(--color-text-tertiary)] text-sm">Compétences: </span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {extractedData.competences
                    .flatMap((c) => c.listComp)
                    .slice(0, 8)
                    .map((skill, idx) => (
                      <span
                        key={idx}
                        className="inline-flex px-2 py-0.5 text-xs rounded bg-bpa-accent-light text-bpa-accent"
                      >
                        {skill}
                      </span>
                    ))}
                  {extractedData.competences.flatMap((c) => c.listComp).length > 8 && (
                    <span className="inline-flex px-2 py-0.5 text-xs text-[var(--color-text-tertiary)]">
                      +{extractedData.competences.flatMap((c) => c.listComp).length - 8} autres
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
